import { Empty } from "@bednbody/ui/components/empty";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { cn } from "@bednbody/ui/lib/utils";
import type { Icon } from "@phosphor-icons/react";
import {
  ArrowsLeftRightIcon,
  CheckCircleIcon,
  MoneyIcon,
  UserCheckIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { ReadFailedCard } from "@bednbody/ui/components/read-failed-card";
import { useMemo } from "react";
import type { useAllCashRemittances, useBookingEventsFeed } from "../command-center.hooks";
import type { useAttendance } from "../ops-dashboard.hooks";

type BookingEvents = NonNullable<ReturnType<typeof useBookingEventsFeed>["data"]>;
type Remittances = NonNullable<ReturnType<typeof useAllCashRemittances>["data"]>;
type AttendanceRows = NonNullable<ReturnType<typeof useAttendance>["data"]>;

interface FeedItem {
  id: string;
  at: string; // ISO
  icon: Icon;
  iconClass: string;
  text: string;
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleDateString("en-PH", { month: "short", day: "numeric" });
}

/**
 * Real events only, merged newest-first: booking status transitions
 * (booking_events — written exclusively by Edge Functions), cash remittance
 * submissions, and today's clock-ins. Nothing synthesized.
 */
export function ActivityFeed({
  events,
  remittances,
  attendance,
  isLoading,
  failed = false,
}: {
  events: BookingEvents;
  remittances: Remittances;
  attendance: AttendanceRows;
  isLoading: boolean;
  /** A failed read is not a quiet day. */
  failed?: boolean;
}) {
  const items = useMemo<FeedItem[]>(() => {
    const list: FeedItem[] = [];

    for (const e of events) {
      const ref = e.bookings?.ref ?? "Booking";
      const hub = e.bookings?.hubs?.name;
      const to = e.to_status.replace(/_/g, " ");
      const isDone = e.to_status === "completed";
      const isCancelled = e.to_status === "cancelled";
      list.push({
        id: `ev-${e.id}`,
        at: e.created_at ?? new Date(0).toISOString(),
        icon: isDone ? CheckCircleIcon : isCancelled ? XCircleIcon : ArrowsLeftRightIcon,
        iconClass: isDone
          ? "bg-success/15 text-success"
          : isCancelled
            ? "bg-destructive/15 text-destructive"
            : "bg-info/15 text-info",
        text: `${ref} → ${to}${hub ? ` (${hub})` : ""}`,
      });
    }

    for (const r of remittances) {
      // ⚠ SHE SUBMITTED NOTHING, SO THE FEED SAYS NOTHING. A ₱0 remittance
      // (0203) means the Therapist was owed at least what she collected and
      // kept all of it; "Maria submitted ₱0 remittance" describes a hand-over
      // that did not happen. Filtered on the amount rather than the stage
      // because this loop is not stage-scoped.
      if (r.amount <= 0) continue;
      list.push({
        id: `rem-${r.id}`,
        at: r.created_at ?? new Date(0).toISOString(),
        icon: MoneyIcon,
        iconClass: "bg-warning/15 text-warning",
        text: `${r.staff?.full_name ?? "Therapist"} submitted ₱${r.amount.toLocaleString("en-PH")} remittance${r.hubs?.name ? ` (${r.hubs.name})` : ""}`,
      });
    }

    for (const a of attendance) {
      if (!a.startedAtIso) continue;
      list.push({
        id: `att-${a.id}-${a.startedAtIso}`,
        at: a.startedAtIso,
        icon: UserCheckIcon,
        iconClass: "bg-primary/10 text-primary-dark dark:text-primary-light",
        text: `${a.name} started their session${a.hubName ? ` at ${a.hubName}` : ""}`,
      });
    }

    return list.sort((a, b) => b.at.localeCompare(a.at)).slice(0, 20);
  }, [events, remittances, attendance]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="size-7 motion-reduce:animate-none" />
            <Skeleton className="h-3 flex-1 motion-reduce:animate-none" />
          </div>
        ))}
      </div>
    );
  }

  if (failed) {
    return (
      <ReadFailedCard message="The feed could not be read. Today's activity is unknown, not absent." />
    );
  }

  if (items.length === 0) {
    return <Empty>No activity yet today.</Empty>;
  }

  return (
    <ul className="max-h-80 space-y-1 overflow-y-auto pr-1">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <li
            key={item.id}
            className="flex items-start gap-3 rounded-xl px-2 py-1.5 hover:bg-background"
          >
            <span
              className={cn(
                "mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full",
                item.iconClass,
              )}
            >
              <Icon size={13} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-foreground">{item.text}</p>
              <p className="text-xs text-muted-foreground">{relativeTime(item.at)}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
